import React from 'react';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import { FieldState } from '@zonofi/zono-money-store';
import { initialPaymentModeData } from '../constant';
import { PaymentFieldKeys } from '../../constants';
import {
  getCurrencySign,
  isValidCurrencyAmount,
} from '@zonofi/common/functions/format-amount';
import { commonInputSx } from '../../helper';

interface PaymentInputFieldProps {
  field: FieldState;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (key: string, value: any) => void;
  index?: number;
}

export const PaymentInputField: React.FC<PaymentInputFieldProps> = ({
  field,
  value,
  onChange,
  index = 0,
}) => {
  const label = `${field.label}${field.required ? '*' : ''}`;

  switch (field.type) {
    case 'dropdown':
      return (
        <TextField
          key={`${field.key}-${index}`}
          select
          label={label}
          value={value ?? ''}
          onChange={(e) => onChange(field.key, e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          sx={commonInputSx}
          InputLabelProps={{
            shrink: Boolean(value && value.trim() !== ''),
          }}
        >
          {initialPaymentModeData.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      );

    case 'date':
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          key={`${field.key}-${index}`}
        >
          <DatePicker
            label={label}
            value={value ? dayjs(value, 'DD/MM/YYYY') : null}
            onChange={(newValue) =>
              onChange(field.key, newValue?.format('DD/MM/YYYY') ?? '')
            }
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                sx: commonInputSx,
                InputLabelProps: {
                  shrink: Boolean(value && value.trim() !== ''),
                },
              },
            }}
          />
        </LocalizationProvider>
      );

    case 'time':
      return (
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          key={`${field.key}-${index}`}
        >
          <TimePicker
            label={label}
            value={value ? dayjs(value, 'HH:mm') : null}
            onChange={(newValue) =>
              onChange(field.key, newValue?.format('HH:mm') ?? '')
            }
            slotProps={{
              textField: {
                fullWidth: true,
                margin: 'normal',
                variant: 'outlined',
                sx: commonInputSx,
                InputLabelProps: {
                  shrink: Boolean(value && value.trim() !== ''),
                },
              },
            }}
          />
        </LocalizationProvider>
      );

    default:
      return (
        <TextField
          key={`${field.key}-${index}`}
          label={label}
          value={
            field.key === PaymentFieldKeys.AMOUNT_PAID
              ? value
                ? `${getCurrencySign()} ${value}`
                : ''
              : (value ?? '')
          }
          onChange={(e) => {
            const input = e.target.value;
            if (field.key === PaymentFieldKeys.AMOUNT_PAID) {
              const rawValue = input.replace(/[^\d.]/g, '');
              if (isValidCurrencyAmount(rawValue)) {
                onChange(field.key, rawValue);
              }
            } else {
              onChange(field.key, input);
            }
          }}
          fullWidth
          margin="normal"
          variant="outlined"
          sx={commonInputSx}
          InputLabelProps={{
            shrink: Boolean(value && value.trim() !== ''),
          }}
        />
      );
  }
};
